export type ArtworkOptimizationPresetId = "quality" | "balanced" | "performance";

export interface ArtworkOptimizationPreset {
  id: ArtworkOptimizationPresetId;
  label: string;
}

export interface ArtworkOptimizationConfig {
  enabled: boolean;
  allowedForCurrentUser: boolean;
  maxActiveJobsPerUser: number;
  platformMaxSourceBytes: number;
  activeWorkerMaxSourceBytes: number;
  contentTypes: string[];
  presets: ArtworkOptimizationPreset[];
}

export const DISABLED_ARTWORK_OPTIMIZATION_CONFIG: ArtworkOptimizationConfig = {
  enabled: false,
  allowedForCurrentUser: false,
  maxActiveJobsPerUser: 3,
  platformMaxSourceBytes: 0,
  activeWorkerMaxSourceBytes: 0,
  contentTypes: [],
  presets: [],
};

export type ArtworkOptimizationJobStatus =
  | "awaiting_upload"
  | "queued"
  | "processing"
  | "ready"
  | "failed"
  | "finalized"
  | "cancelled"
  | "expired";

export interface ArtworkOptimizationProgress {
  stage: string;
  percent: number;
  message?: string;
}

export interface ArtworkOptimizationMetrics {
  sourceBytes?: number;
  optimizedBytes?: number;
  sourceTriangles?: number;
  optimizedTriangles?: number;
  sourceTextureBytes?: number;
  optimizedTextureBytes?: number;
}

export interface ArtworkOptimizationJob {
  jobId: string;
  artworkId: string;
  status: ArtworkOptimizationJobStatus;
  preset: ArtworkOptimizationPresetId;
  progress?: ArtworkOptimizationProgress | null;
  metrics?: ArtworkOptimizationMetrics | null;
  warnings?: string[];
  previewUrl?: string | null;
  failureReason?: string | null;
  artworkDraft?: ArtworkOptimizationArtworkDraft;
}

export interface PendingArtworkOptimizationJob {
  jobId: string;
  artworkId: string;
  status: Extract<ArtworkOptimizationJobStatus, "awaiting_upload" | "queued" | "processing" | "ready" | "failed">;
  preset: ArtworkOptimizationPresetId;
  title: string;
  fileName: string;
  fileSize: number;
  progress?: ArtworkOptimizationProgress;
  metrics?: ArtworkOptimizationMetrics;
  warnings: string[];
  failure: { code: string; message: string } | null;
  createdAt: string | null;
  updatedAt: string | null;
  completedAt: string | null;
}

export interface PendingArtworkOptimizationJobsResponse {
  jobs: PendingArtworkOptimizationJob[];
  visibleCount: number;
  hasMore: boolean;
  quota: {
    activeCount: number;
    maxActiveJobs: number;
  };
}
export interface ArtworkOptimizationArtworkDraft {
  title: string;
  artistName: string;
  description: string[];
  height: string;
  materials: string;
  dateofcreation: string;
  artworkType: "3dmodel";
  tags: string[];
}

export interface CreateArtworkOptimizationJobRequest {
  fileName: string;
  fileSize: number;
  contentType: "model/gltf-binary";
  preset: ArtworkOptimizationPresetId;
  artworkDraft: ArtworkOptimizationArtworkDraft;
}

export interface ArtworkOptimizationUploadInstruction {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expiresAt: string;
}

export interface CreateArtworkOptimizationJobResponse {
  job: ArtworkOptimizationJob;
  upload: ArtworkOptimizationUploadInstruction;
}

export interface RetryArtworkOptimizationUploadRequest {
  fileName: string;
  fileSize: number;
  contentType: "model/gltf-binary";
}

export type RetryArtworkOptimizationUploadResponse = CreateArtworkOptimizationJobResponse;

export type ArtworkOptimizationSelection = "optimized" | "original";
